<template>
    <div class="bg-white p-3 rounded shadow-sm">
        <div class="float-right mb-4">
            <!-- <span class="status" :class="erp.status">{{ erp.status }}</span> -->
            <button class="btn" @click="showIntroMsg = !showIntroMsg">
                {{ showIntroMsg ? 'Hide' : 'Show' }} Guideline
            </button>
        </div>

        <h4 class="mb-3 font-weight-bold">ERP Budgeting</h4>

        <div v-if="parseInt(unclosed) >= 3" class="alert alert-danger">
            You have {{ unclosed }} disbursed ERPs.
            Please close them before creating a new one
        </div>

        <div v-if="erp.code" class="mb-3">
            <h5 class="font-weight-bold">CODE: {{ erp.code }} | {{ erp.currency }} <span
                class="font">{{ money(erp.amount) }}</span>
            </h5>
            {{ erp.title }}
        </div>

        <div v-if="erp.is_draft ==1" class="rounded-sm border p-2 border-danger mb-3 font-weight-bold text-danger">
            This is a draft ERP
        </div>

        <Alerts :_success="success" :_error="error"></Alerts>

        <!-- Create new Budget: No ERP selected -->
        <div v-if="showIntroMsg" class="p-3 bg-light border border-secondary rounded-lg mb-3">
            <h6 class="font-weight-bold">Create new ERP budget.</h6>
            Fill in budget items and price per unit.
            <p class="text-muted">
                The total budget for each item will be automatically calculated for you. After submitting your ERP, you
                can enter the actual expenditures for each item, and the system will calculate the variance.<br>
                If the variance is less than zero, a 'top-up' ERP will be generated for additional funding. Conversely,
                if the
                variance is greater than zero, a refund will be initiated.
            </p>
        </div>

        <!-- Budgeting completed -->
        <div
            v-if="erp.code && erp.budgeted_at && erp.status !== 'pending'"
            class="p-2 border border-success text-success font-weight-bold mb-3 rounded">
            ✅ ERP budgeting is completed <br>
            <b>Date completed:</b> {{ formatDate(erp.budgeted_at) }}
        </div>


        <!-- Budgeting not completed -->
        <div v-if="erp.code && !erp.budgeted_at" class="alert alert-primary mb-3">
            <b>Complete ERP Budgeting</b><br>
            Input the actual value for each item to finalize ERP budgeting. <br>
            A top-up ERP or refund may be generated
            based on the variance. <br>
            <span class="text-muted">Please note that you cannot add or edit list items and their initial values.</span>
            <b>Current Variance: {{ budgeting_currency }} <span class="font">{{ money(grandVariance()) }}</span></b>
        </div>

        <div>
            <div class="border p-3 rounded-lg mb-3">
                <div class="custom-control custom-switch mb-2">
                    <input type="checkbox" class="custom-control-input" id="title-switch" v-model="make_title"
                           @change="setTitle()" :disabled="wait || erp.status !== 'pending' || is_advance">
                    <label class="custom-control-label" for="title-switch">
                        Utilize the names of list items as the title for the ERP?
                        <p class="text-muted">List items/transactions titles will be used as the title of the ERP</p>
                    </label>
                </div>

                <div class="custom-control custom-switch mb-2">
                    <input type="checkbox" class="custom-control-input" id="copy-switch" v-model="copy_budgeting"
                           @change="copyBudgeting()" :disabled="wait || erp.status !=='pending' || is_advance">
                    <label class="custom-control-label" for="copy-switch">
                        Copy Budgeting from another ERP?
                        <p class="text-muted">You can copy budgeting transactions from an existing ERP</p>

                        <select v-if="copy_budgeting" class="form-control mb-2" :disabled="!copy_budgeting"
                                v-model="copy_erp"
                                @change="copyErpSet()">
                            <option value="">--select--</option>
                            <option v-for="i in my_requests" :value="i" :key="i">{{ i.title }}</option>
                        </select>

                        <div v-if="copy_budgeting && copy_erp.code" class="small text-muted border p-2 bg-light">
                            You have copied budgeting items from this ERP. Only budgeting items will be copied.<br>
                            ERP code is {{ copy_erp.code }} and was created on {{ copy_erp.created_at }}.
                        </div>
                    </label>
                </div>

                <div class="custom-control custom-switch mb-4">
                    <input type="checkbox" class="custom-control-input" id="misce-switch" v-model="add_misce"
                           @change="miscellaneousUpdated()" :disabled="wait || erp.status !=='pending' ||  is_advance">
                    <label class="custom-control-label" for="misce-switch">
                        <p>Add a miscellaneous transaction item with a rate of 5% of the total amount.</p>

                        <table v-if="add_misce" class="table table-sm w-50 table-bordered table-top">
                            <tbody>
                            <tr>
                                <td>Total Amount :</td>
                                <td><b>{{ sum('budget') }}</b></td>
                            </tr>
                            <tr>
                                <td>Miscellaneous :</td>
                                <td><b>{{ miscellaneous() }}</b></td>
                            </tr>
                            <tr>
                                <td>Grand Total :</td>
                                <td><b>{{ grandSum('budget') }}</b></td>
                            </tr>
                            </tbody>
                        </table>
                    </label>
                </div>
            </div>

            <section class="mb-3">
                <!--  Currency settings -->
                <div v-if="erp.status === 'pending'" class="mb-2 bg-light rounded-lg p-3">
                    <b>CHANGE CURRENCY TO:</b>&nbsp;
                    <select v-model="budgeting_currency" @change="changeCurrency()" class="btn mb-2">
                        <option>KES</option>
                        <option>USD</option>
                        <option>CAD</option>
                        <option>EUR</option>
                    </select>

                    <div class="custom-control custom-switch">
                        <input type="checkbox" class="custom-control-input" id="currency-switch"
                               v-model="update_amounts">
                        <label class="custom-control-label" for="currency-switch">
                            Update amounts when currency changes.
                        </label>
                    </div>
                </div>

                <div v-if="erp.code && !erp.budgeted_at"
                     class="p-2 rounded bg-light text-teal border font-weight-bold my-3">
                    ✍️ You can now leave comments on your ERP budget items when doing actual
                </div>


                <div class="table-responsive">
                    <button class="btn">Currency: {{ budgeting_currency }}</button>
                    <table class="table border table-sm my-3">
                        <thead class="bg-light">
                        <tr>
                            <th></th>
                            <th style="width: 40%;">ITEM DESCRIPTION</th>
                            <th style="width: 150px" class="text-right">QTY</th>
                            <th style="width: 200px" class="text-right">UNIT COST</th>
                            <th style="width: 140px" class="text-right">BUDGET</th>

                            <!-- When ERP is already created -->
                            <th style="width: 150px" class="text-right">ACTUAL</th>
                            <th style="width: 150px" class="text-right">VARIANCE</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(i, index) in rows" :key="index">
                            <td class="font-weight-bold">{{ ++index }}</td>
                            <td>
                                <textarea class="form-control mb-1 form-control-sm"
                                          :placeholder="`Item ${index} name or description`"
                                          @keyup="setTitle()"
                                          @focus="inputFocused(index)" v-model="i.item" :disabled="disableInputs(i)"
                                          required></textarea>

                                <textarea v-if="!erp.budgeted_at && i.actual" v-model="i.comment" placeholder="Comment"
                                          rows="2"
                                          class="form-control form-control-sm text-teal"
                                          style="font-style: italic; font-size: smaller"></textarea>

                                <div v-else-if="i.comment" class="text-teal text-sm"
                                     style="font-size: small">💬 {{ i.comment }}
                                </div>
                            </td>
                            <td>
                                <input type="number" class="form-control form-control-sm text-right" v-model="i.units"
                                       min="1"
                                       max="100000"
                                       :disabled="disableInputs(i)" required>
                            </td>
                            <td>
                                <input type="number" class="form-control form-control-sm text-right" v-model="i.cost"
                                       min="1"
                                       max="100000"
                                       :disabled="disableInputs(i)" required>
                            </td>
                            <td class="text-right"><input class="form-control form-control-sm text-right"
                                                          :value="money(budget(i))"
                                                          disabled></td>

                            <!-- Actual and variance(computed) only available when ERP is approved -->
                            <td class="text-right">
                                <input type="number" class="form-control form-control-sm text-right" v-model="i.actual"
                                       min="1"
                                       max="100000" required :disabled="allowActuals()" @click="actualClicked(i)"
                                       @change="setTitle()">
                            </td>
                            <td class="text-right"><input class="form-control text-right" :value="variance(i)" disabled>
                            </td>
                            <td class="text-center">
                                <button v-if="erp.status == 'pending'"
                                        class="btn btn-sm btn-outline-danger py-0 px-2 font-weight-bold"
                                        @click="deleteRow(index)"
                                        :disabled="wait">x
                                </button>
                            </td>
                        </tr>
                        <tr v-if="add_misce">
                            <td></td>
                            <td><input disabled value="Miscellaneous (5%)" class="form-control"></td>
                            <td><input disabled :value="1" class="form-control text-right"></td>
                            <td><input disabled :value="miscellaneous()" class="form-control text-right"></td>
                            <td class="text-right">{{ miscellaneous() }}</td>
                            <td class="text-right">
                                <input :disabled="allowActuals()" v-model="actual_misce" type="number"
                                       class="form-control text-right" @change="setTitle()">
                            </td>
                            <td class="text-right">0</td>
                        </tr>
                        </tbody>
                        <tfoot>
                        <tr>
                            <td></td>
                            <td></td>
                            <th class="text-right">{{ sum('units') }}</th>
                            <th class="text-right">{{ money(grandSum('cost')) }}</th>
                            <th class="text-right">{{ money(grandSum('budget')) }}</th>

                            <th class="text-right">{{ money(grandSum('actual')) }}</th>
                            <th class="text-right">{{ money(grandVariance()) }}</th>
                            <th></th>
                        </tr>
                        </tfoot>
                    </table>
                </div>
                <button v-if="!erp.code || erp.status === 'pending'" class="btn btn-primary"
                        :disabled="wait || is_advance"
                        @click="addRow()">
                    Add Row +
                </button>
            </section>


            <div class="mb-3 p-3 border rounded-lg">
                <div v-if="erp.status === 'pending' && !is_advance">
                    <div class="form-group mt-4 mb-3">
                        <label class="font-weight-bold">Select Budget Line</label>
                        <select class="form-control mb-2" v-model="datum.budget_id" :disabled="wait" required>
                            <option value="">--Select--</option>
                            <optgroup v-for="(options, key) in budgets" v-bind:label="key" :key="key">
                                <option v-for="b in options" :key="b.id" :value="b.id">{{ b.name }}</option>
                            </optgroup>
                        </select>
                        <p v-if="!datum.budget_id" class="text-muted">Leave this empty if you do not know the budget</p>
                    </div>

                    <div class="custom-control custom-switch mb-3">
                        <input type="checkbox" class="custom-control-input" id="title-switch" v-model="make_title"
                               @change="setTitle()">
                        <label class="custom-control-label" for="title-switch">
                            Utilize the names of list items as the title for the ERP?
                        </label>
                    </div>

                    <div class="form-group">
                        <label class="font-weight-bold">Title of the ERP <span class="text-danger">*</span></label>
                        <textarea class="form-control mb-2" v-model="datum.title" rows="3" :disabled="wait" required
                                  placeholder="What title would you want to give your ERP?"></textarea>
                        <p v-if="!datum.title" class="text-muted">ERP title is required</p>

                    </div>
                </div>

                <!-- If the overflow is greater than zero, we create a refund, else we create another top up erp-->
                <section v-if="erp && !erp.budgeted_at && erp.status !== 'pending'">
                    <div v-if="grandVariance() < 0">
                        <h4 class="font-weight-bold">Create Top-up Request</h4>
                        <p>There is a deficit of <b>{{ budgeting_currency }} {{ grandVariance() }}</b></p>
                        <div class="form-group mt-1">
                            <label class="font-weight-bold">Top-Up ERP<span class="text-danger">*</span></label>
                            <textarea class="form-control mb-2" v-model="datum.topup" rows="2" :disabled="wait" required
                                      placeholder="Top Up ERP title"></textarea>
                            <p v-if="!datum.topup" class="text-muted">Top up ERP title is required</p>

                        </div>
                    </div>

                    <div v-if="grandVariance() > 0">
                        <h4 class="font-weight-bold">Create Refund</h4>
                        <p class="font-weight-bold">
                            There is an overflow of <b>{{ grandVariance() }}</b>.
                            This will create a refund under the ERP above.
                        </p>
                        <p>
                            All remaining money after expenditure to be remitted back to the bank.
                            Please use the following MPESA Paybill and account :
                        </p>
                        <p class="text-muted">
                            Here is the correct procedure for refunds: <br>
                            Step 1 - Fill in the budget with actual figures <br>
                            Step 2 - Use the company Paybill number <br>
                            Step 3 - Key in the M-Pesa code in the system. <br>
                        </p>
                        <p>
                            Paybill: <b>880100</b> <br>
                            Account No: <b>8137450018</b>
                        </p>

                        <div class="form-group mt-1">
                            <label>Select Payment Method</label>
                            <select v-model="datum.mode" class="form-control" :disabled="wait">
                                <option value="M-Pesa">M-Pesa</option>
                                <option value="Bank">Bank Transfer</option>
                            </select>
                        </div>

                        <div class="form-group mt-1">
                            <label>Enter {{ datum.mode }} Reference Code<sup class="text-danger">*</sup></label>
                            <input type="text" class="form-control"
                                   :placeholder="`Enter ${datum.mode} reference code required`"
                                   v-model="datum.reference">
                        </div>
                    </div>
                </section>
            </div>

            <!--  File upload -->
            <Documents v-if="erp.code" :code="code" class="mt-4"></Documents>

            <!--  Tag users -->
            <div class="my-3 border p-3 rounded-lg">
                <label class="col-form-label text-md-right font-weight-bold mr-2">Tag Users</label>&nbsp;
                <div class="custom-control custom-switch custom-control-inline mb-1">
                    <input type="checkbox" class="custom-control-input" id="tag_all_users" v-model="tag_all_users">
                    <label class="custom-control-label" for="tag_all_users">Tag all users</label>
                </div>
                <multiselect
                    class="mb-2"
                    v-model="tagged_users"
                    :options="users"
                    :disabled="wait"
                    :multiple="true"
                    track-by="id"
                    :preserve-search="true"
                    :hide-selected="true"
                    placeholder="Select user to tag" label="name" @input="setTaggedUsersIds">
                </multiselect>
                <span class="text-muted">Tagged users can view your ERP but cannot edit</span>
            </div>

            <button @click="process('no')" class="btn btn-primary" :disabled="wait || !datum.title">
                <span v-if="wait">Please wait...</span>
                <span v-else>Submit</span>
            </button>
            &nbsp;
            <button v-if="erp.status=='pending' && !erp.budgeted_at" class="btn btn-dark"
                    @click="process('yes')"
                    :disabled="wait || erp.is_draft">
                Save as Draft
            </button>
        </div>
    </div>
</template>

<script>

import Alerts from "@/components/Alerts.vue";
import Multiselect from "vue-multiselect";
import Documents from './Documents.vue'

export default {
    name: "RequestsBudgeting",
    components: {Multiselect, Alerts, Documents},
    props: ['code', 'auth'],
    data() {
        return {
            axios: this.$http,
            base_url: "",

            formData: new FormData(),
            unclosed: 0,

            budgets: [],
            budgeting_currency: 'KES',
            update_amounts: false,

            showIntroMsg: false,
            make_title: true,

            add_misce: false,
            actual_misce: 0,
            is_advance: false,

            users: [],
            tagged_users: [],
            tag_all_users: false,

            copy_budgeting: false,
            copy_erp: {},

            my_requests: [],

            rate: 100,

            datum: {
                title: '',
                topup: '',
                method: '',
                mode: 'M-Pesa',
                budget_id: '',
                tagged: ''
            },

            erp: {code: null, status: 'pending', documents: []},

            rows: []
        }
    },
    mounted() {
        if (this.code) {
            this.loadErp()
        }

        this.axios.get(`${this.base_url}/requests/unclosed?check=1`).then(res => {
            this.unclosed = res.data.data
        })

        this.axios.get(`${this.base_url}/budgets`).then(res => {
            this.budgets = res.data.data
        })

        this.axios.get(this.base_url + `/users`).then(res => {
            this.users = res.data.data
        })
    },
    watch: {
        tag_all_users() {
            (this.tag_all_users == true) ? this.tagged_users = this.users : this.tagged_users = []
            this.setTaggedUsersIds(this.tagged_users)
        }
    },
    computed: {
        getRows() {
            return this.rows
        },
        getAuth() {
            return JSON.parse(this.auth)
        }
    },
    methods: {
        setTaggedUsersIds(users = []) {
            let ids = [];
            users.forEach((i) => {
                ids.push(i.id);
            });
            this.datum.tagged = ids;
        },
        changeCurrency() {
            if (this.update_amounts) {
                this.rows.forEach(row => {
                    // Convert cost
                    let amount = parseFloat(row.cost);
                    let convertedCost = (this.budgeting_currency !== 'KES') ? (amount / this.rate) : (amount * this.rate);
                    row.cost = convertedCost.toFixed(2);

                    // Convert variance
                    let variance = parseFloat(row.variance);
                    let convertedVariance = (this.budgeting_currency !== 'KES') ? (variance / this.rate) : (variance * this.rate);
                    row.variance = convertedVariance.toFixed(2);
                });
            }
        },

        loadErp() {
            this.axios.get(`${this.base_url}/request/json/` + this.code).then(res => {
                this.erp = res.data.data
                this.budgeting_currency = this.erp.budgeting_currency

                this.tagged_users = this.erp.tagged
                this.setTaggedUsersIds(this.tagged_users)

                if (this.erp.budgeting) {
                    this.add_misce = this.erp.miscellaneous
                    this.datum.title = this.erp.title
                    this.datum.budget_id = this.erp.budget_id
                    this.rows = (this.erp.budgeting)
                }
                this.setTitle()
            })
        },
        allowActuals() {
            return (this.wait
                || !this.erp.code
                || this.erp.status === 'pending'
                || this.erp.budgeted_at
            )
        },
        disableInputs() {
            return (this.wait || this.erp.status != 'pending')
        },
        setTitle() {
            let _title = this.rows.map(a => a.item).toString()
            if ((_title.toLowerCase()).startsWith('salary advance')) {
                this.add_misce = this.make_title = this.copy_budgeting = false
                this.is_advance = true
                this.datum.title = _title

            } else {
                this.is_advance = false

                if (this.make_title) {
                    this.datum.title = 'To Pay ' + _title
                } else {
                    this.datum.title = ''
                }
            }

            if (this.erp) {
                this.datum.topup = `Top-Up: ${this.erp.title}`
            }
        },
        sum(col) {
            let total = this.rows.reduce((sum, row) => {
                return sum += parseFloat(row[col]);
            }, 0);
            return !isNaN(total) ? total : 0
        },
        grandVariance() {
            if (this.add_misce) {
                return (this.sum('variance'))
            }
            return this.sum('variance')
        },
        grandSum(col) {
            if (this.add_misce) {
                return parseFloat(this.sum(col)) + this.miscellaneous()
            }
            return this.sum(col)
        },
        miscellaneous() {
            return parseFloat((this.sum('budget') * 0.05).toFixed(2))
        },
        miscellaneousVariance() {
            let a_misc = parseFloat(this.actual_misce)
            if (isNaN(a_misc)) {
                a_misc = 0
            }
            return (this.miscellaneous() - a_misc)
        },
        budget(row) {
            if (row.cost) {
                row.budget = (parseInt(row.units) * parseFloat(row.cost))
            }
            return row.budget
        },

        variance(row) {
            if (row.actual) {
                row.variance = (row.budget - parseFloat(row.actual))
            }
            return row.variance
        },
        addRow() {
            this.rows.push({
                item: '',
                units: 1,
                cost: 0,
                budget: 0,
                actual: 0,
                variance: 0,
                comment: ''
            })
            this.setTitle()
        },
        deleteRow(index) {
            this.rows.splice(index, 1);
            this.setTitle()
        },
        actualClicked(row) {
            if (row.actual == 0) {
                row.actual = this.budget(row)
            }
        },
        miscellaneousUpdated() {
            this.setTitle()
            this.computeMiscellaneous()
        },
        copyErpSet() {
            if (this.copy_erp) {
                this.rows = (this.copy_erp.budgeting).map(a => {
                    a.actual = 0
                    a.comment = ''
                    return a
                })
            } else {
                this.rows = []
                this.addRow()
            }
        },
        copyBudgeting() {
            if (this.copy_budgeting) {
                this.axios.get(`${this.base_url}/requests/mine`).then(res => {
                    this.my_requests = res.data.data
                })
            }
        },
        computeMiscellaneous() {
            const misce_index = this.rows.findIndex(row => row.item === 'Miscellaneous');
            if (misce_index !== -1) {
                const amount = Math.round(this.sum('budget') * 0.05);
                Object.assign(this.rows[misce_index], {
                    units: 1,
                    cost: amount,
                    budget: amount,
                    actual: amount
                });
            }
        },

        prepareData(asDraft) {
            let variance = this.grandVariance();
            let type = (variance > 0) ? 'refund' : 'topup';

            this.datum.budget = this.sum('budget')
            this.datum.actuals = this.grandSum('actual')

            this.datum.variance = variance
            this.datum.type = type

            // Add the rows tobe saved as ERP budget
            this.datum.rows = JSON.stringify(this.rows)
            this.datum.code = this.code
            this.datum.currency = this.budgeting_currency
            this.datum.miscellaneous = (this.add_misce == true) ? "yes" : 'no'
            this.datum.draft = asDraft

            Object.entries(this.datum).forEach(([key, value]) => this.formData.append(key, value));

            this.success = "Loading, please wait..";
        },

        process(asDraft) {
            window.scroll(0, 0)
            this.error = this.success = ''

            this.prepareData(asDraft)
            this.wait = true

            this.axios.post(`${this.base_url}/request/budget`, this.formData, {
                headers: {'Content-Type': 'multipart/form-data'},
            }).then(res => {
                this.handleSuccess(res.data.data.codes);
                localStorage.removeItem('rows')

            }).catch(error => {
                this.handleError(error);
            }).then(() => {
                this.wait = false
            })

            // }
        },
        handleSuccess(codes) {
            window.scroll(0, 0);
            this.success = 'Saved successfully!';
            if (this.code) {
                this.loadErp()
            } else {
                setTimeout(() => window.location.href = `/requests?codes=${codes}`, 600);
            }
        },

        handleError(error) {
            window.scroll(0, 0);
            this.showError(error);
        }
    }
}
</script>

<style scoped>
input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Firefox */
input[type=number] {
    -moz-appearance: textfield;
}

table input, table select, table textarea {
    width: 100%;
    padding: 1px 10px !important;
}

.table-top tr td:first-child {
    text-align: right;
}
</style>
